import { apiErrorMessage } from "@/lib/api";
import { API_URL, apiFetch } from "@/lib/api-client";
import { createPasskey, getPasskey, signalUnknownPasskey } from "@/lib/webauthn";

/** パスワード確認後に求められる 2 段目。verify は登録済みの手段で確認、enroll は組織が必須で未登録のため登録。 */
export type MfaChallenge =
	| { mode: "verify"; totp: boolean; passkey: boolean; recoveryCode: boolean }
	| { mode: "enroll"; passkeyAvailable: boolean };

export interface TotpEnrollment {
	secret: string;
	/** 認証アプリに読み取らせる otpauth URI。 */
	uri: string;
	state: string;
}

export interface RegisteredPasskey {
	id: string;
	name: string;
	createdAt: string;
	lastUsedAt: string | null;
	credentialId: string;
}

export interface AccountMfa {
	required: boolean;
	totpEnabled: boolean;
	passkeys: RegisteredPasskey[];
	recoveryCodesRemaining: number;
	passkeyAvailable: boolean;
	/** 本人確認をし直してから時間が経っておらず、手段を変更できるか。 */
	reauthenticated: boolean;
	rpId: string | null;
	userHandle: string;
}

/** 手段を登録した結果。初めての手段ならリカバリーコードが発行される。 */
export type RecoveryCodes = string[] | null;

interface Ceremony<T> {
	options: T;
	state: string;
}

/** ログイン途中の確認が期限切れ・無効なとき。パスワードから入り直してもらう。 */
export class MfaSessionExpiredError extends Error {
	constructor() {
		super("時間が経ったため、もう一度サインインしてください。");
		this.name = "MfaSessionExpiredError";
	}
}

/** 手段の変更の前に、本人確認をし直す必要があるとき。 */
export class MfaReauthRequiredError extends Error {
	constructor() {
		super("続けるには本人確認が必要です。");
		this.name = "MfaReauthRequiredError";
	}
}

/** サーバーが応答したエラー。status で、資格情報が保存されなかったかを見分ける。 */
export class MfaRequestError extends Error {
	constructor(
		message: string,
		readonly status: number,
	) {
		super(message);
		this.name = "MfaRequestError";
	}
}

/**
 * 作成したパスキーをサーバーに送る。サーバーが検証して拒否した (400) ときだけ、端末からも消させる。
 * 通信断や 5xx ではサーバーに保存済みのことがあるため消させない。
 */
async function submitNewPasskey<T>(
	options: PublicKeyCredentialCreationOptionsJSON,
	credentialId: string,
	submit: () => Promise<T>,
): Promise<T> {
	try {
		return await submit();
	} catch (err) {
		if (err instanceof MfaRequestError && err.status === 400) {
			signalUnknownPasskey(options.rp.id ?? location.hostname, credentialId).catch(
				(signalErr: unknown) =>
					console.error(
						"拒否されたパスキーをパスワードマネージャーに知らせられませんでした",
						signalErr,
					),
			);
		}
		throw err;
	}
}

// --- パスキーだけのサインイン ---

export type PasskeySignInCeremony = Ceremony<PublicKeyCredentialRequestOptionsJSON>;

const TOO_MANY_ATTEMPTS_MESSAGE = "試行回数が多すぎます。しばらくしてからもう一度お試しください。";

/** メールアドレスを聞かずにパスキーでサインインするチャレンジ。この環境でパスキーを使えなければ null。 */
export async function startPasskeySignIn(): Promise<PasskeySignInCeremony | null> {
	const res = await fetch(`${API_URL}/api/auth/passkey/options`, {
		method: "POST",
		credentials: "include",
	});
	if (res.status === 409) return null;
	if (res.status === 429) throw new Error(TOO_MANY_ATTEMPTS_MESSAGE);
	if (!res.ok) throw new Error(await apiErrorMessage(res, "パスキーを準備できませんでした"));
	return (await res.json()) as PasskeySignInCeremony;
}

export async function completePasskeySignIn(state: string, credential: unknown): Promise<void> {
	const res = await fetch(`${API_URL}/api/auth/passkey`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify({ state, credential }),
	});
	if (res.status === 401)
		throw new Error(
			"パスキーでサインインできませんでした。もう一度お試しいただくか、メールアドレスとパスワードでサインインしてください。組織で接続元を制限している場合は、許可されたネットワークから接続してください。",
		);
	if (res.status === 429) throw new Error(TOO_MANY_ATTEMPTS_MESSAGE);
	if (!res.ok) throw new Error(await apiErrorMessage(res, "パスキーでサインインできませんでした"));
}

// --- ログインの 2 段目 ---
// 401 はログイン待ちの期限切れを意味する。apiFetch の 401 リダイレクトでは理由が伝わらないため fetch を使う。

async function loginStep<T>(path: string, body: unknown, fallback: string): Promise<T> {
	const res = await fetch(`${API_URL}/api/auth/mfa/${path}`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify(body ?? {}),
	});
	if (res.status === 401) throw new MfaSessionExpiredError();
	if (!res.ok) throw new MfaRequestError(await apiErrorMessage(res, fallback), res.status);
	return (await res.json()) as T;
}

export async function verifyTotpLogin(code: string): Promise<void> {
	await loginStep("totp", { code }, "コードを確認できませんでした");
}

export async function verifyRecoveryCodeLogin(code: string): Promise<void> {
	await loginStep("recovery-code", { code }, "リカバリーコードを確認できませんでした");
}

/** パスキーで確認する。取り消されたら false。 */
export async function verifyPasskeyLogin(): Promise<boolean> {
	const ceremony = await loginStep<Ceremony<PublicKeyCredentialRequestOptionsJSON>>(
		"passkey/options",
		{},
		"パスキーを準備できませんでした",
	);
	const credential = await getPasskey(ceremony.options);
	if (!credential) return false;
	await loginStep(
		"passkey",
		{ state: ceremony.state, credential },
		"パスキーを確認できませんでした",
	);
	return true;
}

export function startTotpEnrollmentLogin(): Promise<TotpEnrollment> {
	return loginStep("enroll/totp/start", {}, "認証アプリの登録を始められませんでした");
}

export async function completeTotpEnrollmentLogin(
	state: string,
	code: string,
): Promise<RecoveryCodes> {
	const data = await loginStep<{ recoveryCodes: RecoveryCodes }>(
		"enroll/totp",
		{ state, code },
		"認証アプリを登録できませんでした",
	);
	return data.recoveryCodes;
}

/** ログイン途中にパスキーを登録する。取り消されたら undefined。 */
export async function enrollPasskeyLogin(): Promise<RecoveryCodes | undefined> {
	const ceremony = await loginStep<Ceremony<PublicKeyCredentialCreationOptionsJSON>>(
		"enroll/passkey/options",
		{},
		"パスキーの登録を始められませんでした",
	);
	const credential = await createPasskey(ceremony.options);
	if (!credential) return undefined;
	const data = await submitNewPasskey(ceremony.options, credential.id, () =>
		loginStep<{ recoveryCodes: RecoveryCodes }>(
			"enroll/passkey",
			{ state: ceremony.state, credential },
			"パスキーを登録できませんでした",
		),
	);
	return data.recoveryCodes;
}

// --- 自分の設定 ---

async function accountRequest<T>(
	path: string,
	init: RequestInit,
	fallback: string,
): Promise<T | undefined> {
	const res = await apiFetch(`/api/account/mfa${path}`, init);
	if (res.status === 403 && (await isReauthRequired(res))) throw new MfaReauthRequiredError();
	if (!res.ok) throw new MfaRequestError(await apiErrorMessage(res, fallback), res.status);
	return res.status === 204 ? undefined : ((await res.json()) as T);
}

async function isReauthRequired(res: Response): Promise<boolean> {
	const body: unknown = await res
		.clone()
		.json()
		.catch(() => null);
	return (
		typeof body === "object" &&
		body !== null &&
		(body as { type?: unknown }).type === "reauth_required"
	);
}

export async function getAccountMfa(): Promise<AccountMfa> {
	return (await accountRequest<AccountMfa>(
		"",
		{},
		"多要素認証の設定を取得できませんでした",
	)) as AccountMfa;
}

/** 手段をまだ持たないときだけ使える、パスワードでの本人確認。 */
export async function reauthWithPassword(password: string): Promise<void> {
	await accountRequest(
		"/reauth/password",
		{ method: "POST", body: JSON.stringify({ password }) },
		"パスワードを確認できませんでした",
	);
}

export async function reauthWithTotp(code: string): Promise<void> {
	await accountRequest(
		"/reauth/totp",
		{ method: "POST", body: JSON.stringify({ code }) },
		"コードを確認できませんでした",
	);
}

export async function reauthWithRecoveryCode(code: string): Promise<void> {
	await accountRequest(
		"/reauth/recovery-code",
		{ method: "POST", body: JSON.stringify({ code }) },
		"リカバリーコードを確認できませんでした",
	);
}

/** パスキーで本人確認する。取り消されたら false。 */
export async function reauthWithPasskey(): Promise<boolean> {
	const ceremony = (await accountRequest<Ceremony<PublicKeyCredentialRequestOptionsJSON>>(
		"/reauth/passkey/options",
		{ method: "POST" },
		"パスキーを準備できませんでした",
	)) as Ceremony<PublicKeyCredentialRequestOptionsJSON>;
	const credential = await getPasskey(ceremony.options);
	if (!credential) return false;
	await accountRequest(
		"/reauth/passkey",
		{ method: "POST", body: JSON.stringify({ state: ceremony.state, credential }) },
		"パスキーを確認できませんでした",
	);
	return true;
}

export async function startTotpEnrollment(): Promise<TotpEnrollment> {
	return (await accountRequest<TotpEnrollment>(
		"/totp/start",
		{ method: "POST" },
		"認証アプリの登録を始められませんでした",
	)) as TotpEnrollment;
}

export async function completeTotpEnrollment(state: string, code: string): Promise<RecoveryCodes> {
	const data = await accountRequest<{ recoveryCodes: RecoveryCodes }>(
		"/totp",
		{ method: "POST", body: JSON.stringify({ state, code }) },
		"認証アプリを登録できませんでした",
	);
	return data?.recoveryCodes ?? null;
}

export async function disableTotp(): Promise<void> {
	await accountRequest("/totp", { method: "DELETE" }, "認証アプリを削除できませんでした");
}

/** パスキーを登録する。取り消されたら undefined。 */
export async function registerPasskey(name: string): Promise<RecoveryCodes | undefined> {
	const ceremony = (await accountRequest<Ceremony<PublicKeyCredentialCreationOptionsJSON>>(
		"/passkeys/options",
		{ method: "POST" },
		"パスキーの登録を始められませんでした",
	)) as Ceremony<PublicKeyCredentialCreationOptionsJSON>;
	const credential = await createPasskey(ceremony.options);
	if (!credential) return undefined;
	const data = await submitNewPasskey(ceremony.options, credential.id, () =>
		accountRequest<{ recoveryCodes: RecoveryCodes }>(
			"/passkeys",
			{ method: "POST", body: JSON.stringify({ state: ceremony.state, credential, name }) },
			"パスキーを登録できませんでした",
		),
	);
	return data?.recoveryCodes ?? null;
}

export async function deletePasskey(passkeyId: string): Promise<void> {
	await accountRequest(
		`/passkeys/${encodeURIComponent(passkeyId)}`,
		{ method: "DELETE" },
		"パスキーを削除できませんでした",
	);
}

export async function regenerateRecoveryCodes(): Promise<string[]> {
	const data = await accountRequest<{ recoveryCodes: string[] }>(
		"/recovery-codes",
		{ method: "POST" },
		"リカバリーコードを再発行できませんでした",
	);
	return data?.recoveryCodes ?? [];
}

// --- 管理者 ---

export async function getOrganizationMfaRequired(organizationId: string): Promise<boolean> {
	const res = await apiFetch(`/api/organizations/${encodeURIComponent(organizationId)}/mfa`);
	if (!res.ok)
		throw new Error(await apiErrorMessage(res, "多要素認証の設定を取得できませんでした"));
	return ((await res.json()) as { required: boolean }).required;
}

export async function setOrganizationMfaRequired(
	organizationId: string,
	required: boolean,
): Promise<boolean> {
	const res = await apiFetch(`/api/organizations/${encodeURIComponent(organizationId)}/mfa`, {
		method: "PUT",
		body: JSON.stringify({ required }),
	});
	if (!res.ok)
		throw new Error(await apiErrorMessage(res, "多要素認証の設定を変更できませんでした"));
	return ((await res.json()) as { required: boolean }).required;
}

export async function resetUserMfa(userId: string): Promise<void> {
	const res = await apiFetch(`/api/users/${encodeURIComponent(userId)}/mfa/reset`, {
		method: "POST",
	});
	if (!res.ok) throw new Error(await apiErrorMessage(res, "多要素認証をリセットできませんでした"));
}
